import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Payment as PaymentIcon,
  Subscriptions as SubscriptionIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { fetchDashboardStats } from '../store/slices/dashboardSlice';
import { RootState, AppDispatch } from '../store';


const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { stats, loading, error } = useSelector((state: RootState) => state.dashboard);
  

  useEffect(() => {
    dispatch(fetchDashboardStats());
  }, [dispatch]);
  
  

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount);
  };

  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon, 
    color = 'primary',
    progress 
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
    progress?: number;
  }) => (
    <Card sx={{ 
      height: '100%',
      border: '1px solid #e5e7eb',
      borderRadius: 2,
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }
    }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '1.875rem',
                lineHeight: 1.2,
                color: '#111827'
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${color}.50`,
            color: `${color}.600`
          }}>
            {React.cloneElement(icon as React.ReactElement, { fontSize: 'medium' } as any)}
          </Box>
        </Box>
        
        {subtitle && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            {subtitle}
          </Typography>
        )}
        
        {progress !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              color={color}
              sx={{ 
                height: 4, 
                borderRadius: 2,
                backgroundColor: `${color}.100`,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>        
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>
          ダッシュボード
        </Typography>
      </Box>
        
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            borderRadius: 2,
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2'
          }}
        >
          データの取得に失敗しました: {error}
        </Alert>
      )}

      {stats && (
        <>
          {/* 統計カード */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                title="総決済数"
                value={stats.payments.total?.toLocaleString() || '0'}
                subtitle={`今日: ${stats.payments.today || 0}件`}
                icon={<PaymentIcon />}
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                title="アクティブサブスクリプション"
                value={stats.subscriptions.active?.toLocaleString() || '0'}
                subtitle={`成長率: +${stats.subscriptions.growth || 0}%`}
                icon={<SubscriptionIcon />}
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                title="総顧客数"
                value={stats.customers?.total?.toLocaleString() || '0'}
                subtitle={`新規: ${stats.customers?.new || 0}人`}
                icon={<PeopleIcon />}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                title="今月の売上"
                value={formatCurrency(stats.revenue?.thisMonth || 0)}
                subtitle={`前月比: +${stats.revenue?.growth || 0}%`}
                icon={<TrendingUpIcon />}
                color="success"
              />
            </Grid>
          </Grid>
        </>
      )}

      {stats && (
        <>
          {/* 詳細統計 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#111827',
                    mb: 3
                  }}
                >
                  決済統計詳細
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6 }}>
                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 1 }}
                      >
                        成功決済
                      </Typography>
                      <Typography 
                        variant="h5" 
                        color="success.main" 
                        sx={{ fontWeight: 700, fontSize: '1.5rem' }}
                      >
                        {stats.payments.successful?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 1 }}
                      >
                        失敗決済
                      </Typography>
                      <Typography 
                        variant="h5" 
                        color="error.main" 
                        sx={{ fontWeight: 700, fontSize: '1.5rem' }}
                      >
                        {stats.payments.failed?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                        >
                          成功率
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                        >
                          {stats.payments.successRate}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.payments.successRate} 
                        color="success"
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          backgroundColor: '#dcfce7',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ 
                p: 3,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#111827',
                    mb: 3
                  }}
                >
                  サブスクリプション状況
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Chip 
                      label={`アクティブ: ${stats.subscriptions.active}`} 
                      color="success" 
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: 1
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Chip 
                      label={`カード登録待ち: ${stats.subscriptions.pendingCardRegistration || 0}`} 
                      color="info" 
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: 1
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Chip 
                      label={`支払い遅延: ${stats.subscriptions.pastDue}`} 
                      color="warning" 
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: 1
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Chip 
                      label={`キャンセル: ${stats.subscriptions.cancelled}`} 
                      color="default" 
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: 1
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* 最近のアクティビティ */}
          <Paper sx={{ 
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
          }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  color: '#111827'
                }}
              >
                最近のアクティビティ
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#f9fafb' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151' }}>
                      タイプ
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151' }}>
                      ステータス
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151' }}>
                      詳細
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151' }}>
                      顧客ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151' }}>
                      時刻
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats.recentActivities || stats.recentTransactions || []).map((activity, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9fafb' },
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ mr: 2, color: '#6b7280' }}>
                            {(activity.type || 'payment') === 'payment' ? 
                              <PaymentIcon fontSize="small" /> : 
                              <SubscriptionIcon fontSize="small" />
                            }
                          </Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {(activity.type || 'payment') === 'payment' ? '決済' : 'サブスクリプション'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            activity.status === 'completed' ? '完了' :
                            activity.status === 'failed' ? '失敗' :
                            activity.status === 'created' ? '作成' :
                            activity.status === 'cancelled' ? 'キャンセル' : activity.status
                          }
                          color={
                            activity.status === 'completed' ? 'success' :
                            activity.status === 'failed' ? 'error' :
                            activity.status === 'created' ? 'info' :
                            activity.status === 'cancelled' ? 'default' : 'default'
                          }
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          {formatCurrency(activity.amount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {activity.customer || activity.customerName || activity.customerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {activity.time || new Date(activity.createdAt).toLocaleString('ja-JP')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default Dashboard;